import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type ActionResult } from '@/lib/api'
import { centeredSwal, showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { PasswordInput } from '@/components/PasswordInput'
import { LegalModals } from '@/features/account/LegalModals'
import { sendOtp } from '@/features/account/otp'
import {
  calculatePasswordStrength,
  isValidEmail,
  meetsPasswordRule,
} from '@/features/account/passwordStrength'

/**
 * Port of Views/Account/Register.cshtml + wwwroot/js/app/account/register.js.
 *
 * Validation order is preserved exactly, messages included: passwords match ->
 * terms agreed -> email format -> password strength >= 60. The server re-checks
 * the first two, but the client messages differ from the server's, and these are
 * the ones users see.
 *
 * Two notes on the original:
 *  - `initPasswordStrengthMeter` looked for `#passwordStrength` with
 *    `.strength-fill` / `.strength-text` children. No such markup exists in
 *    Register.cshtml, so the meter never rendered — while `validateForm` still
 *    rejected anything under 60. Users got "Password is too weak" with no
 *    indication of what would be strong enough. The gate is kept (removing it
 *    would let weaker passwords through than before); adding the missing meter
 *    would be a new feature, so it is flagged rather than built.
 *  - On success the original called `sendOTPAfterRegistration()` AND
 *    `showSuccessAndRedirect()` back to back without awaiting the first. Both
 *    opened their own dialog and both scheduled a redirect, so two SweetAlerts
 *    raced and whichever timer fired first won. Here the OTP is awaited and then
 *    the success dialog shows — the sequence the code was clearly reaching for.
 */
export function Register(): JSX.Element {
  usePageTitle('Register', 'auth')
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  function validate(): string | null {
    if (password !== confirmPassword) return 'Passwords do not match!'
    if (!agreeToTerms) return 'You must agree to the terms and conditions!'
    if (!isValidEmail(email)) return 'Please enter a valid email address!'
    if (calculatePasswordStrength(password).percentage < 60) {
      return 'Password is too weak. Please use a stronger password.'
    }
    // The server's rule, checked here so it is not discovered as a bare
    // "Invalid input data." after submitting. See passwordStrength.ts.
    if (!meetsPasswordRule(password)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number.'
    }
    return null
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    const problem = validate()
    if (problem) {
      await showError(problem)
      return
    }

    setIsSubmitting(true)
    try {
      const response = await api.post<ActionResult>('/api/ecareer/account/register', {
        FullName: fullName,
        Email: email,
        PhoneNumber: phoneNumber,
        Password: password,
        ConfirmPassword: confirmPassword,
        AgreeToTerms: agreeToTerms,
      })

      if (!response.success) {
        await showError(response.message ?? 'Registration failed!')
        return
      }

      // Registration succeeded; the account still needs the emailed OTP.
      //
      // The destination stays the one register returned. Overwriting it with
      // send-otp's `redirectUrl` sent newly registered users to the FORGOT-
      // PASSWORD verification page, which then confirmed their code as a
      // password reset and dropped them on /Account/ResetPassword.
      // register.js:166-171 kept `response.redirectTo` for the same reason.
      const redirectUrl = response.redirectTo ?? '/Account/ConfirmEmailRegist'
      try {
        const otp = await sendOtp(email, 'register')
        if (!otp.success) {
          await showError(
            otp.message ??
              'Registration successful but failed to send verification email. Please try again.',
          )
          return
        }
      } catch {
        await showError(
          'Registration successful but failed to send verification email. Please try again from login page.',
        )
        return
      }

      await centeredSwal({
        icon: 'success',
        title: 'Success!',
        text: 'Registration successful! Please check your email for the verification code.',
        timer: 3000,
        showConfirmButton: false,
        allowOutsideClick: false,
        timerProgressBar: true,
      })
      navigate(redirectUrl)
    } catch {
      await showError('An error occurred during registration. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className="text-center">
        <img
          src="/lib/images/authentication/img-auth-register.png"
          alt="Register"
          className="img-fluid mb-3"
        />
        <h4 className="f-w-500 mb-1">Register with your email</h4>
        <p className="mb-3">
          Already have an Account?
          <Link to="/Account/Login" className="link-primary">
            Log in
          </Link>
        </p>
      </div>

      <form id="registerForm" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            id="FullName"
            name="FullName"
            placeholder="Full Name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
            autoComplete="given-name"
          />
        </div>

        <div className="row">
          <div className="col-sm-6">
            <div className="mb-3">
              <input
                type="email"
                className="form-control"
                id="Email"
                name="Email"
                placeholder="Email Address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
              />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-3">
              <input
                type="tel"
                className="form-control"
                id="PhoneNumber"
                name="PhoneNumber"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                autoComplete="tel"
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <PasswordInput
            id="Password"
            value={password}
            onChange={setPassword}
            placeholder="Password"
            required
            autoComplete="new-password"
          />
        </div>

        <div className="mb-3">
          <PasswordInput
            id="ConfirmPassword"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirm Password"
            required
            autoComplete="new-password"
          />
        </div>

        <div className="d-flex mt-1 justify-content-between">
          <div className="form-check">
            <input
              className="form-check-input input-primary"
              type="checkbox"
              name="AgreeToTerms"
              id="AgreeToTerms"
              checked={agreeToTerms}
              onChange={(event) => setAgreeToTerms(event.target.checked)}
              required
            />
            <label className="form-check-label text-muted" htmlFor="AgreeToTerms">
              I agree to the{' '}
              <a
                href="#"
                className="link-primary"
                data-bs-toggle="modal"
                data-bs-target="#modalToS"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="#"
                className="link-primary"
                data-bs-toggle="modal"
                data-bs-target="#modalPrivacy"
              >
                Privacy Policy
              </a>
            </label>
          </div>
        </div>

        <div className="d-grid mt-4">
          <button type="submit" className="btn btn-primary" id="registerBtn" disabled={isSubmitting}>
            {isSubmitting ? 'Registering...' : 'Create Account'}
          </button>
        </div>
      </form>

      <LegalModals />
    </>
  )
}
